import { useState, useEffect, useMemo } from 'react';
import { supabase } from '../services/supabaseClient.ts';

interface UseFileContentResult {
  text: string | null;
  loading: boolean;
  error: string | null;
  isBinary: boolean;
}

interface UseFileContentCache {
  [key: string]: UseFileContentResult;
}

// Cache to avoid duplicate fetches for the same scan+file combination
const fileContentCache: UseFileContentCache = {};

/**
 * Clears the file content cache - useful for testing
 */
export function clearFileContentCache(): void {
  Object.keys(fileContentCache).forEach(key => {
    delete fileContentCache[key];
  });
}

/**
 * Custom hook to fetch real file content from uploaded ZIP files
 * 
 * @param scanId - The scan ID to fetch content for
 * @param filePath - The file path within the ZIP to extract
 * @returns Object with text, loading, error, and isBinary states
 */
export function useFileContent(scanId: string | null, filePath: string | null): UseFileContentResult {
  const [result, setResult] = useState<UseFileContentResult>({
    text: null,
    loading: false,
    error: null,
    isBinary: false,
  });

  // Create cache key for memoization
  const cacheKey = useMemo(() => {
    if (!scanId || !filePath) return null;
    return `${scanId}:${filePath}`;
  }, [scanId, filePath]);

  useEffect(() => {
    // Early return if no scan ID or file path
    if (!scanId || !filePath || !supabase) {
      setResult({
        text: null,
        loading: false,
        error: null,
        isBinary: false,
      });
      return;
    }

    // Check cache first
    if (cacheKey && fileContentCache[cacheKey]) {
      console.log('[useFileContent] Using cached result for:', cacheKey);
      setResult(fileContentCache[cacheKey]);
      return;
    }

    // Start loading
    console.log('[useFileContent] Fetching content for:', { scanId, filePath });
    setResult(prev => ({ ...prev, loading: true, error: null }));

    const fetchFileContent = async () => {
      try {
        // Check if we're in demo mode
        const isDemoMode = import.meta.env.VITE_DEMO_MODE === 'true';
        
        if (isDemoMode || scanId.startsWith('demo-')) {
          console.log('[useFileContent] 🎭 Demo mode detected, using mock data');
          
          // Simulate loading delay
          await new Promise(resolve => setTimeout(resolve, 300));
          
          // Return mock content based on file type
          const mockContent = getMockFileContent(filePath);
          
          const mockResult: UseFileContentResult = {
            text: mockContent,
            loading: false,
            error: null,
            isBinary: mockContent === null,
          };

          setResult(mockResult);
          if (cacheKey) fileContentCache[cacheKey] = mockResult;
          return;
        }

        // Real mode - get current session for JWT token
        const { data: { session }, error: sessionError } = await supabase.auth.getSession();
        
        if (sessionError || !session?.access_token) {
          throw new Error('No valid session found');
        }

        console.log('[useFileContent] Making API request to get-file-content...');
        
        // Call the get-file-content Edge Function
        const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
        const response = await fetch(`${supabaseUrl}/functions/v1/get-file-content`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${session.access_token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            scan_id: scanId,
            file_path: filePath
          }),
        });

        console.log('[useFileContent] API response status:', response.status);

        if (response.status === 412) {
          // Binary file or too large
          const errorData = await response.json();
          console.log('[useFileContent] Binary file detected:', errorData);
          
          const binaryResult: UseFileContentResult = {
            text: null,
            loading: false,
            error: null,
            isBinary: true,
          };

          setResult(binaryResult);
          if (cacheKey) fileContentCache[cacheKey] = binaryResult;
          return;
        }

        if (!response.ok) {
          // Other error (403, 404, 500, etc.)
          const errorData = await response.json();
          
          // Handle 404 errors specifically for missing file content
          if (response.status === 404) {
            console.log('[useFileContent] 📁 File not found (404), providing fallback message');
            console.log('[useFileContent] Error details:', errorData);
            
            // Use the already parsed errorData instead of parsing again
            const errorMessage = errorData.error || 'File content not found';
            const suggestion = errorData.suggestion || 'Re-upload your ZIP file or connect a GitHub repository to view code.';
            
            let fallbackContent = `// ${errorMessage}\n//\n// ${suggestion}\n//\n`;
            
            // Add debug info if available
            if (errorData.debug_info) {
              const debugInfo = errorData.debug_info;
              fallbackContent += `// Scan Details:\n`;
              fallbackContent += `// • Scan ID: ${debugInfo.scan_id}\n`;
              fallbackContent += `// • Source Type: ${debugInfo.source_type}\n`;
              fallbackContent += `// • Status: ${debugInfo.status}\n`;
              if (debugInfo.created_at) {
                fallbackContent += `// • Created: ${new Date(debugInfo.created_at).toLocaleDateString()}\n`;
              }
            }
            
            fallbackContent += `//\n// This is not an error with SafeVibe - some scans simply\n// don't have preserved file content depending on how they\n// were created or their age.\n`;
            
            const fallbackResult: UseFileContentResult = {
              text: fallbackContent,
              loading: false,
              error: null,
              isBinary: false,
            };

            setResult(fallbackResult);
            if (cacheKey) fileContentCache[cacheKey] = fallbackResult;
            return;
          }
          
          throw new Error(errorData.error || `HTTP ${response.status}`);
        }

        // Success - get text content
        const text = await response.text();
        console.log('[useFileContent] ✅ Successfully fetched file content, length:', text.length);

        const successResult: UseFileContentResult = {
          text,
          loading: false,
          error: null,
          isBinary: false,
        };

        setResult(successResult);
        if (cacheKey) fileContentCache[cacheKey] = successResult;

      } catch (err) {
        console.error('[useFileContent] ❌ Failed to fetch file content:', err);
        
        const errorResult: UseFileContentResult = {
          text: null,
          loading: false,
          error: (err as Error)?.message || 'Failed to load file content',
          isBinary: false,
        };

        setResult(errorResult);
        if (cacheKey) fileContentCache[cacheKey] = errorResult;
      }
    };

    fetchFileContent();
  }, [scanId, filePath, supabase, cacheKey]);

  return result;
}

/**
 * Get mock file content for demo mode
 */
function getMockFileContent(filePath: string): string | null {
  // Return null for binary files (will be treated as binary)
  if (filePath.match(/\.(jpg|jpeg|png|gif|pdf|zip|exe|bin|dll|so|dylib|tar|gz|rar|7z|mp3|mp4|avi|mov|ico|woff|woff2|ttf|eot)$/i)) {
    return null;
  }

  // Common demo files with secrets
  const mockFiles: Record<string, string> = {
    'src/config.js': `// Configuration file with exposed secrets
const API_CONFIG = {
  openai: {
    apiKey: "sk-1234567890abcdef1234567890abcdef1234567890abcdef", // 🚨 EXPOSED!
    baseUrl: "https://api.openai.com/v1"
  },
  supabase: {
    url: "https://xyzproject.supabase.co",
    anonKey: "eyJ0eXAiOiJKV1QiLCJhbGciOiJIUzI1NiJ9..." // 🚨 PUBLIC KEY!
  },
  stripe: {
    publishableKey: "pk_test_51234567890abcdef...", // Should be public
    secretKey: "sk_test_51234567890abcdef..." // 🚨 SECRET EXPOSED!
  }
};

export default API_CONFIG;`,

    'src/auth.js': `// Authentication logic with hardcoded credentials
import bcrypt from 'bcrypt';

const ADMIN_CREDENTIALS = {
  username: "admin",
  password: "supersecret123", // 🚨 HARDCODED PASSWORD!
  email: "admin@company.com"
};

export function validateUser(username, password) {
  // Insecure comparison
  if (username === ADMIN_CREDENTIALS.username && 
      password === ADMIN_CREDENTIALS.password) {
    return { valid: true, role: 'admin' };
  }
  return { valid: false };
}

// Database connection with exposed credentials
const DB_URL = "postgresql://user:password123@localhost:5432/production"; // 🚨 EXPOSED!`,

    'package.json': `{
  "name": "vulnerable-app",
  "version": "1.0.0",
  "description": "Demo app with security issues",
  "main": "index.js",
  "scripts": {
    "start": "node src/index.js",
    "dev": "nodemon src/index.js"
  },
  "dependencies": {
    "express": "^4.18.0",
    "bcrypt": "^5.0.1",
    "jsonwebtoken": "^8.5.1"
  },
  "author": "Developer",
  "license": "MIT"
}`,

    '.env': `# Environment variables - should never be committed!
NODE_ENV=production
DATABASE_URL=postgresql://admin:secretpass123@db.company.com:5432/prod
JWT_SECRET=my-super-secret-jwt-key-12345
STRIPE_SECRET_KEY=sk_live_abcdef1234567890
OPENAI_API_KEY=sk-1234567890abcdef1234567890abcdef1234567890abcdef

# Third-party integrations
SENDGRID_API_KEY=SG.xyz123456789...
AWS_ACCESS_KEY_ID=AKIAIOSFODNN7EXAMPLE
AWS_SECRET_ACCESS_KEY=wJalrXUtnFEMI/K7MDENG/bPxRfiCYEXAMPLEKEY`,

    'README.md': `# Vulnerable Demo App

This is a demo application that contains several common security vulnerabilities for educational purposes.

## Setup

1. Clone the repository
2. Install dependencies: \`npm install\`
3. Start the server: \`npm start\`

## Features

- User authentication
- API integrations
- File uploads
- Payment processing

## Known Issues

This demo contains intentional security issues:
- Exposed API keys in configuration files
- Hardcoded passwords
- Insecure authentication logic
- Environment variables committed to git

**DO NOT USE IN PRODUCTION!**`,

    'src/index.js': `// Main application entry point
const express = require('express');
const app = express();

// Hardcoded secrets (bad practice!)
const JWT_SECRET = "hardcoded-jwt-secret-123";
const ADMIN_TOKEN = "admin-access-token-xyz789";

app.use(express.json());

// Insecure endpoint
app.get('/admin', (req, res) => {
  const token = req.headers.authorization;
  if (token === ADMIN_TOKEN) {
    res.json({ message: 'Admin access granted', users: getAllUsers() });
  } else {
    res.status(401).json({ error: 'Unauthorized' });
  }
});

const PORT = process.env.PORT || 3000;
app.listen(PORT, () => {
  console.log(\`Server running on port \${PORT}\`);
});`
  };

  // Return file content if found, otherwise generate generic content
  if (mockFiles[filePath]) {
    return mockFiles[filePath];
  }

  // Generate content based on file extension
  const ext = filePath.split('.').pop()?.toLowerCase();
  
  switch (ext) {
    case 'js':
      return `// Generated mock content for ${filePath}
const config = {
  apiKey: "demo-api-key-12345",
  secret: "demo-secret-token" // This is just demo content
};

export default config;`;
    
    case 'json':
      return `{
  "name": "demo-file",
  "description": "Mock content for ${filePath}",
  "version": "1.0.0"
}`;
    
    case 'md':
      return `# ${filePath}

This is mock content for demonstration purposes.

The real file content would appear here when viewing actual scan results.`;
    
    default:
      return `Mock content for ${filePath}

This file contains demonstration content.
In a real scan, you would see the actual file content from your uploaded codebase.

File type: ${ext || 'unknown'}
Path: ${filePath}`;
  }
}

 